// features/videos/RailVideoItem.tsx — a single "up next" rail row: small thumb +
// 2-line title + author + views/age. Horizontal variant of VideoCard for the
// watch page's right rail.
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { MetaRow } from '@/ui';
import { formatViews } from './meta';
import { VideoThumb } from './VideoThumb';
import type { VideoItem, VideoCreator } from '@/core/models/video';

const Row = styled(Link)`
  display: flex;
  gap: 10px;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 4px;
  transition: background 120ms ease;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const ThumbWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 128px;
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
`;

const Text = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Title = styled.h4`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface RailVideoItemProps {
  video: VideoItem;
  creator?: VideoCreator;
  /** Where the row goes — the watch page by default; Flash's Up next opens the Flash player. */
  to?: string;
}

export function RailVideoItem({ video, creator, to }: RailVideoItemProps) {
  return (
    // No aria-label override here on purpose — the link's accessible name is
    // computed from its own text content (title + creator + views/age), which
    // is more informative for screen readers than the title alone.
    <Row to={to ?? `/videos/${video.id}`}>
      <ThumbWrap>
        <VideoThumb video={video} compact />
      </ThumbWrap>
      <Text>
        <Title>{video.title}</Title>
        {creator && <Meta>{creator.name}</Meta>}
        <Meta>
          <MetaRow>
            <span>{formatViews(video.views)}</span>
            <span>{video.ageLabel}</span>
          </MetaRow>
        </Meta>
      </Text>
    </Row>
  );
}
